import type { ShouldRevalidateFunction } from 'react-router';

/** How one search param differs between two addresses. */
type Change = 'added' | 'removed' | 'changed';

/**
 * The search params that differ between two addresses, by name: added, removed, or changed. A
 * repeated param is compared whole (its values, in their order); the order of the params is not.
 */
function changedParams(from: URLSearchParams, to: URLSearchParams): Map<string, Change> {
  const changes = new Map<string, Change>();
  for (const key of new Set([...from.keys(), ...to.keys()])) {
    const before = from.getAll(key);
    const after = to.getAll(key);
    if (before.length === 0) changes.set(key, 'added');
    else if (after.length === 0) changes.set(key, 'removed');
    else if (before.length !== after.length || before.some((value, i) => value !== after[i]))
      changes.set(key, 'changed');
  }
  return changes;
}

/**
 * A route's shouldRevalidate: skip the loaders when the only change in the address is one the
 * screen handles on its own (APP-135 §4). The router re-runs a route's loaders whenever the search
 * changes and only moves to the new address once they finish, so a list paged on the screen would
 * wait for its whole list to be read again. With the loaders skipped the page changes at once; the
 * page's own queries still refresh stale data in the background.
 *
 * - `screenParams`: params the screen handles itself, changed in any way (a screen-paged list's
 *   page and rows per page).
 * - `sizeRemoved`: rows-per-page params that may leave the address. After a size is saved,
 *   `usePaging` drops it from the address (§7); the loader would read the same page at the same
 *   size, now the saved one, so it needn't run. A size that changes or arrives still runs it.
 *
 * Anything else (another path, a form or action, a revalidation of the same address, any other
 * param) gets the router's own answer.
 */
export function skipRevalidationFor(
  screenParams: readonly string[],
  opts: { sizeRemoved?: readonly string[] } = {},
): ShouldRevalidateFunction {
  const screen = new Set(screenParams);
  const removable = new Set(opts.sizeRemoved ?? []);
  return ({
    currentUrl,
    nextUrl,
    formMethod,
    formAction,
    actionResult,
    defaultShouldRevalidate,
  }) => {
    if (currentUrl.pathname !== nextUrl.pathname) return defaultShouldRevalidate;
    if (formMethod !== undefined || formAction !== undefined || actionResult !== undefined)
      return defaultShouldRevalidate;
    // The same address is a revalidation (or the same link pressed again): never skipped.
    if (currentUrl.search === nextUrl.search) return defaultShouldRevalidate;
    for (const [key, change] of changedParams(currentUrl.searchParams, nextUrl.searchParams))
      if (!screen.has(key) && !(change === 'removed' && removable.has(key)))
        return defaultShouldRevalidate;
    return false;
  };
}
